import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import Slider from "src/components/slider";
import Button from "src/components/button";
import Reveal from "src/components/animation";
import { Input } from "src/components/input";
import Thumbnail from "src/components/thumbnail";
import { Banner } from "src/types/common";
import { Media } from "src/types/media";
import { RewardThumbnailPicker } from "../rewardThumbnailPicker";

import {
  createReward,
  updateReward,
} from "src/redux/actions/coaching/gamification";
import { RewardResponse } from "src/types/coaching/gamification";

import _ from "lodash";
import cx from "classnames";
import styles from "./createReward.module.scss";

interface CreateRewardProps {
  show: boolean;
  onClose: () => void;
  rewardToEdit?: RewardResponse | null;
}

export const CreateReward: React.FC<CreateRewardProps> = ({ show, onClose, rewardToEdit = null }) => {
  const dispatch = useDispatch();
  const [rewardName, setRewardName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [coinsRequired, setCoinsRequired] = useState<string>("");
  const [quantityMode, setQuantityMode] = useState<"limited" | "unlimited">("unlimited");
  const [limitedQuantity, setLimitedQuantity] = useState<string>("");
  const [thumbnailBanner, setThumbnailBanner] = useState<Banner | null>(null);
  const [showThumbnailPicker, setShowThumbnailPicker] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const hasPendingRedemptions = _.size(_.get(rewardToEdit, "pendingRedemptionIds", [])) > 0;

  const { savingReward } = useSelector((state: any) => {
    const creating = _.get(state, "coaching.gamification.createReward.loading", false) as boolean;
    const updating = _.get(state, "coaching.gamification.updateReward.loading", false) as boolean;
    return { savingReward: creating || updating };
  });

  useEffect(() => {
    if (!show) return;

    setRewardName("");
    setDescription("");
    setCoinsRequired("");
    setQuantityMode("unlimited");
    setLimitedQuantity("");
    setThumbnailBanner(null);
    setShowThumbnailPicker(false);
    setFieldErrors({});

    if (rewardToEdit) {
      const unlimited = _.get(rewardToEdit, "quantity", 0) < 0;
      const bannerUrl = _.get(rewardToEdit, "banner", "");
      setRewardName(rewardToEdit.name);
      setDescription(_.get(rewardToEdit, "description", ""));
      setCoinsRequired(String(rewardToEdit.pointsCost));
      setQuantityMode(unlimited ? "unlimited" : "limited");
      setLimitedQuantity(unlimited ? "" : String(rewardToEdit.quantity));
      setThumbnailBanner(_.get(rewardToEdit, "banner", null) as Banner | null);    }
  }, [show, rewardToEdit?.id]);

  const validateForm = () => {
    const errors: any = {};
  
    if (_.isEmpty(rewardName) || _.isEmpty(_.trim(rewardName))) {
      errors.name = "Please provide a valid name";
    }
  
    if (_.isEmpty(description) || _.isEmpty(_.trim(description))) {
      errors.description = "Please provide a valid description";
    }
  
    const coins = _.trim(coinsRequired);
    if (_.isEmpty(coins)) {
      errors.coinsRequired = "Please enter the number of points required";
    } else {
      const coinsValue = Number(coins);
      if (!Number.isInteger(coinsValue) || coinsValue < 1) {
        errors.coinsRequired = "Please enter a valid number of points";
      }
    }
  
    if (quantityMode === "limited") {
      const quantity = _.trim(limitedQuantity);
      if (_.isEmpty(quantity)) {
        errors.quantity = "Please enter a quantity";
      } else {
        const quantityValue = Number(quantity);
        if (!Number.isInteger(quantityValue) || quantityValue < 1) {
          errors.quantity = "Please enter a valid quantity";
        }
      }
    }
  
    return errors;
  };

  const handleConfirm = () => {
    const errors = validateForm();
    setFieldErrors(errors);
    if (_.size(errors) > 0) return;

    let banner;

    if (thumbnailBanner) {
      banner = {
        ...thumbnailBanner,
        background: thumbnailBanner.background?.id,
      };
    }  

    const payload = {
      name: _.trim(rewardName),
      description: _.trim(description),
      pointsCost: Number(coinsRequired),
      quantity: quantityMode === "unlimited" ? -1 : Number(limitedQuantity),
      banner: banner,
    };

    if (rewardToEdit) {
      dispatch(updateReward({ id: rewardToEdit.id, ...payload }));
    } else {
      dispatch(createReward(payload));
    }
  };

  return (
    <>
      <Slider.Dialog
        className={styles.dialog}
        width="400px"
        show={show}
        onClose={onClose}
        placement="start"
      >
        <div className={styles.createRewardSlider}>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h2 className={styles.title}>
                {rewardToEdit ? "edit reward" : "create reward"}
              </h2>
              <p className={styles.subtitle}>
                {rewardToEdit
                  ? "Edit reward details that learners can see and redeem."
                  : "Create rewards learners can redeem using earned points."}
              </p>
              {hasPendingRedemptions && (
                <p className={styles.pendingWarning}>
                  <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                  <span>This reward has pending redemptions and cannot be edited.</span>
                </p>
              )}
            </div>
          </div>

          <Slider.Body className={styles.formBody}>
            <div className={styles.field}>
              <span className={styles.label}>Reward Name</span>
              <Input
                className={styles.textInput}
                disabled={hasPendingRedemptions}
                value={rewardName}
                placeholder="Type your reward name"
                maxLength={50}
                onChange={(e) => {
                  setRewardName(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, name: null }));
                }}
              />
              <Reveal show={!!fieldErrors.name}>
                <div className={styles.fieldError}>
                  <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                  {fieldErrors.name}
                </div>
              </Reveal>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Description</span>
              <textarea
                className={styles.textarea}
                disabled={hasPendingRedemptions}
                value={description}
                placeholder="Type your description..."
                maxLength={150}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, description: null }));
                }}
              />
              <Reveal show={!!fieldErrors.description}>
                <div className={styles.fieldError}>
                  <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                  {fieldErrors.description}
                </div>
              </Reveal>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Points Required</span>
              <Input
                className={styles.textInput}
                disabled={hasPendingRedemptions}
                value={coinsRequired}
                placeholder="number of points required..."
                onChange={(e) => {
                  setCoinsRequired(e.target.value.replace(/\D/g, ""));
                  setFieldErrors((prev) => ({ ...prev, coinsRequired: null }));
                }}
              />
              <Reveal show={!!fieldErrors.coinsRequired}>
                <div className={styles.fieldError}>
                  <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                  {fieldErrors.coinsRequired}
                </div>
              </Reveal>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Quantity Available</span>
              <div className={styles.quantityRow}>
                <div className={styles.quantityToggle}>
                  <button
                    type="button"
                    className={cx(styles.segment, {
                      [styles.segmentSelected]: quantityMode === "limited",
                    })}
                    aria-pressed={quantityMode === "limited"}
                    disabled={hasPendingRedemptions}
                    onClick={() => {
                      setQuantityMode("limited");
                      setLimitedQuantity((q) =>
                        _.isEmpty(_.trim(q)) ? "20" : q
                      );
                      setFieldErrors((prev) => ({ ...prev, quantity: null }));
                    }}
                  >
                    {quantityMode === "limited" && (
                      <i className={cx("fa-solid fa-check", styles.segmentCheck)} aria-hidden />
                    )}
                    Limited
                  </button>
                  <div className={styles.divider} />
                  <button
                    type="button"
                    className={cx(styles.segment, {
                      [styles.segmentSelected]: quantityMode === "unlimited",
                    })}
                    aria-pressed={quantityMode === "unlimited"}
                    disabled={hasPendingRedemptions}
                    onClick={() => {
                      setQuantityMode("unlimited");
                      setFieldErrors((prev) => ({ ...prev, quantity: null }));
                    }}
                  >
                    {quantityMode === "unlimited" && (
                      <i className={cx("fa-solid fa-check", styles.segmentCheck)} aria-hidden />
                    )}
                    Unlimited
                  </button>
                </div>
                {quantityMode === "limited" && (
                  <Input
                    className={cx(styles.textInput, styles.quantityLimitInput)}
                    value={limitedQuantity}
                    placeholder=""
                    disabled={hasPendingRedemptions}
                    onChange={(e) => {
                      setLimitedQuantity(e.target.value.replace(/\D/g, ""));
                      setFieldErrors((prev) => ({ ...prev, quantity: null }));
                    }}
                  />
                )}
              </div>
              {quantityMode === "limited" && (
                <p className={styles.quantityHint}>
                  (Add the total number of available units of this award)
                </p>
              )}
              <Reveal show={!!fieldErrors.quantity}>
                <div className={styles.fieldError}>
                  <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                  {fieldErrors.quantity}
                </div>
              </Reveal>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Optional thumbnail</span>
              <button
                type="button"
                className={styles.thumbnailDrop}
                disabled={hasPendingRedemptions}
                onClick={() => setShowThumbnailPicker(true)}
              >
                {thumbnailBanner ? (
                  <Thumbnail
                    media={thumbnailBanner.background}
                    crop={thumbnailBanner.crop}
                    position={thumbnailBanner.position}
                    className={styles.thumbnailPreview}
                    defaultImage="/images/collections/background.jpeg"
                    alt=""
                  />
                ) : (
                  <>
                    <i
                      className={cx(styles.thumbnailIcon, "paperflite-add-image")}
                      aria-hidden
                    />
                    <p className={styles.thumbnailHint}>
                      Click to pick or upload an image
                    </p>
                  </>
                )}
              </button>
            </div>
          </Slider.Body>

          <Slider.Actions>
            <div className={styles.footer}>
              <Button
                variant="primary"
                text={savingReward ? "Creating..." : "Confirm"}
                icon="paperflite-tick"
                className={styles.confirmButton}
                disabled={savingReward || hasPendingRedemptions}
                onClick={handleConfirm}
              />
              <Button
                variant="secondary"
                text="Cancel"
                className={styles.cancelButton}
                disabled={savingReward}
                onClick={onClose}
              />
            </div>
          </Slider.Actions>
        </div>
      </Slider.Dialog>

      <RewardThumbnailPicker
        show={show && showThumbnailPicker}
        onClose={() => setShowThumbnailPicker(false)}
        onFinish={(banner) => {
          setThumbnailBanner(banner);
        }}
      />
    </>
  );
};